import { useRef, useState } from "haunted";
import { html } from "lit-html";
import * as dayjs from "dayjs";
import * as CustomParseFormat from "dayjs/plugin/customParseFormat";
import classNames from "classnames";
import { CLASS_NAMES } from "Services/ClassNames";
import { ref } from "Components/directives/ref";
import { capitalizeAndRemoveDot } from "Services/common";
import { useEffectWithCleanup } from "Shared/haunted/CustomHooks";
import { useWindowSize } from "Shared/haunted/useWindowSize";
import { useNewFlightDatePickerDropdown } from "./useNewFlightDatePickerDropdown";
import { DynamicSettings } from "Components/admin/DynamicSettings";
import getShowFarePriceForCulture from "Shared/helpers/farePriceHelper";
import { useReduxState } from "Shared/redux/useReduxState";
import { Schedule } from "Shared/models/Schedule";
dayjs.extend(CustomParseFormat);

export interface Props {
	availableSchedules: Schedule[];
	culture: string;
	disabledDates?: dayjs.Dayjs[];
	dynamicSettings: DynamicSettings;
	inputTestId?: string;
	isDisabled?: boolean;
	isLoading: boolean;
	isReturnDate: boolean;
	max?: dayjs.Dayjs;
	min?: dayjs.Dayjs;
	placeHolder: string;
	rangeStart?: dayjs.Dayjs;
	value: dayjs.Dayjs;
	onChange: (date: dayjs.Dayjs) => void;
}

export function useNewFlightDatePicker(props: Props) {
	const root = useRef<HTMLDivElement>(undefined);

	const windowSize = useWindowSize();

	const [currentSearch] = useReduxState("currentSearch");

	const inputField = useRef<HTMLInputElement>(undefined);
	const datePicker = useRef<HTMLDivElement>(undefined);

	const [isOpen, setIsOpen] = useState<boolean>(false);

	const dropdown = useNewFlightDatePickerDropdown({
		availableSchedules: props.availableSchedules,
		culture: props.culture,
		disabledDates: props.disabledDates || [],
		max: props.max,
		min: props.min,
		rangeStart: props.rangeStart,
		usePrices:
			getShowFarePriceForCulture(props.dynamicSettings.ShowFarePricesCultures, props.culture) &&
			!currentSearch.useAmericanMiles,
		value: props.value,
		onChange: (data) => handleChange(data),
	});

	const init = () => {
		const onClickOutside = (e: any) => {
			const path = e.path || (e.composedPath && e.composedPath());
			const isClickInside = path.indexOf(inputField.current) > -1 || path.indexOf(datePicker.current) > -1;

			if (!isClickInside) setIsOpen(false);
		};

		const onScroll = () => setIsOpen(false);

		document.addEventListener("click", onClickOutside);
		document.addEventListener("scroll", onScroll);

		return () => {
			document.removeEventListener("click", onClickOutside);
			document.removeEventListener("scroll", onScroll);
		};
	};

	// DEVNOTE The dropdown is aligned to the bottom right corner of the anchor element as per JS design
	const anchorElement = () => {
		if ((windowSize.size === "XS" || windowSize.size === "SM") && !props.isReturnDate) return undefined;

		const anchorPointClass = props.isReturnDate
			? CLASS_NAMES.FLIGHT_CHANGE_SEARCH_RETURN_DATE_CLASS
			: CLASS_NAMES.FLIGHT_CHANGE_SEARCH_OUTBOUND_DATE_CLASS;

		return document.querySelector(`.${anchorPointClass}`);
	};

	const displayedValue = () => (props.value ? capitalizeAndRemoveDot(props.value.format("ddd DD-MM")) : "");

	const handleClick = (e: MouseEvent) => {
		e.preventDefault();
		if (props.isDisabled) return;

		setIsOpen(!isOpen);
	};

	const handleChange = (date: dayjs.Dayjs) => {
		props.onChange(date);
		setIsOpen(false);
	};

	useEffectWithCleanup(init, []);

	const loaderTemplate = () =>
		props.isLoading
			? html`
					<div class="loader-container">
						<div class="loader-line">
							<div class="loader-line-wrap"></div>
						</div>
						<div class="loader-line">
							<div class="loader-line-wrap"></div>
						</div>
						<div class="loader-line">
							<div class="loader-line-wrap"></div>
						</div>
					</div>
			  `
			: "";

	const iconTemplate = () =>
		!props.isLoading
			? html` <i class="jsh-calendar absolute right-4 top-1/2 -translate-y-1/2 transform"></i> `
			: "";

	const dropdownTemplate = () => html`
		<ac-dropdown2
			.anchorElement=${anchorElement()}
			.content=${dropdown.htmlTemplate()}
			.isOpen=${isOpen && !props.isLoading}
			.isOutbound=${!props.isReturnDate}
		></ac-dropdown2>
	`;

	const inputTemplate = () => html`
		<input
			ref=${ref(inputField)}
			class=${classNames(
				"w-full cursor-pointer appearance-none border-none bg-transparent px-0 pb-0 pt-0.5 text-base outline-none",
				"placeholder:text-base placeholder:text-[#c1c1c1]",
				props.isReturnDate
					? CLASS_NAMES.FLIGHT_CHANGE_SEARCH_RETURN_DATE_CLASS
					: CLASS_NAMES.FLIGHT_CHANGE_SEARCH_OUTBOUND_DATE_CLASS,
				{
					"text-n-blue": displayedValue(),
				}
			)}
			data-required
			data-test-id=${props.inputTestId || ""}
			placeholder=${props.placeHolder}
			readonly
			value=${displayedValue()}
			.value=${displayedValue()}
			@click=${handleClick}
		/>
	`;

	const htmlTemplate = () => html`
		<div
			ref=${ref(root)}
			class=${classNames(
				"relative flex h-[34px] w-full cursor-pointer items-center rounded-lg border border-solid bg-white px-4",
				"md:h-full",
				{
					"border-[#c1c1c1] text-[#c1c1c1]": !isOpen && !displayedValue(),
					"border-n-cyan text-n-cyan": isOpen && !displayedValue(),
					"border-n-blue text-n-blue": displayedValue(),
					"pointer-events-none opacity-75": props.isDisabled,
					[CLASS_NAMES.FLIGHT_CHANGE_SEARCH_RETURN_DATE_CLASS]: props.isReturnDate,
				}
			)}
		>
			${inputTemplate()} ${iconTemplate()} ${dropdownTemplate()} ${loaderTemplate()}
		</div>
	`;

	return { htmlTemplate, open: () => setIsOpen(true) };
}
